import React from 'react';
import { DocumentViewRendererProps } from './types';
import { TotalSummary } from '../form/views/cpt/TotalSummary';
import { TimelineView } from '../form/views/cpt/TimelineView';
import { CptCodesView } from '../form/views/cpt/CptCodesView';
import {
    CPT_TASK_LOG_TONE,
    isCptTaskLogSummaryField,
    isCptTaskLogTimelineField,
} from '../form/views/cpt/constants';
import { AttestationAlert } from '../form/views/AttestationAlert';
import { ContextListView } from '../form/views/ContextListView';
import { RequirementStatusView } from '../form/views/RequirementStatusView';
import { StatusListData } from '../types';

export const DocumentViewRenderer: React.FC<DocumentViewRendererProps> = ({
    field,
    watch,
    contextData = {},
    updateData,
    functions,
}) => {
    // Helper to get value from watch or fall back to context data
    const getValue = (key: string) => {
        const watchedValue = watch(key);
        if (watchedValue !== undefined) {
            return watchedValue;
        }
        return contextData[key];
    };

    const viewHandlerKey =
        field.functionKey ||
        (field.viewType === 'cpt-codes' ? 'cptCodes' : field.id);
    const customView = functions?.viewHandlers[viewHandlerKey];
    if (customView) {
        const dependencyName = field.viewProps?.dependency || 'allTasks';
        const dependentValue = getValue(dependencyName);
        return customView({
            field,
            value: dependentValue,
            updateData,
        });
    }

    if (field.viewType === 'total-summary') {
        const dependencyName = field.viewProps?.dependency || 'allTasks';
        const dependentValue = getValue(dependencyName);
        return (
            <TotalSummary
                value={dependentValue}
                sumField={field.viewProps?.sumField || 'minutes'}
                {...field.viewProps}
                tone={
                    isCptTaskLogSummaryField(field.id)
                        ? CPT_TASK_LOG_TONE
                        : undefined
                }
            />
        );
    }

    if (field.viewType === 'timeline') {
        const dependencyName = field.viewProps?.dependency || 'allTasks';
        const dependentValue = getValue(dependencyName);
        return (
            <TimelineView
                value={dependentValue}
                sumField={field.viewProps?.sumField || 'minutes'}
                {...field.viewProps}
                tone={
                    isCptTaskLogTimelineField(field.id)
                        ? CPT_TASK_LOG_TONE
                        : undefined
                }
            />
        );
    }

    if (field.viewType === 'cpt-codes') {
        const dependencyName = field.viewProps?.dependency || 'allTasks';
        const dependentValue = getValue(dependencyName);
        return (
            <CptCodesView
                value={dependentValue}
                sumField={field.viewProps?.sumField || 'minutes'}
                onCodesChange={(codes) => {
                    if (updateData) {
                        updateData('cptCodes', codes);
                    }
                }}
                {...field.viewProps}
            />
        );
    }

    if (field.viewType === 'attestation-alert') {
        // Field ids are configurable via viewProps so a document can carry
        // several attestation blocks; the legacy ids remain the defaults.
        const fieldIdProp = (key: string, fallback: string): string =>
            typeof field.viewProps?.[key] === 'string'
                ? field.viewProps[key]
                : fallback;
        const attested = getValue(fieldIdProp('attestedFieldId', 'attested'));
        const attestedBy = getValue(
            fieldIdProp('attestedByFieldId', 'attestedBy')
        );
        const attestationName = getValue('attestationName');
        const attestedAt = getValue(
            fieldIdProp('attestedAtFieldId', 'attestedAt')
        );
        const signOffDate = getValue('signOffDate');

        // Signature requests: resolve the selected signer's name so the
        // pending state can say who was asked to sign.
        const signerFieldId =
            typeof field.viewProps?.signerFieldId === 'string'
                ? field.viewProps.signerFieldId
                : undefined;
        const signerId = signerFieldId ? getValue(signerFieldId) : undefined;
        const departmentUsers = getValue('departmentUsers');
        const pendingSignerName = signerId
            ? (Array.isArray(departmentUsers)
                  ? departmentUsers.find(
                        (user: any) => user?.value === signerId
                    )?.label
                  : undefined) || 'a colleague'
            : undefined;

        return (
            <AttestationAlert
                attested={attested}
                attestationName={attestedBy || attestationName}
                signOffDate={attestedAt || signOffDate}
                pendingSignerName={pendingSignerName}
            />
        );
    }

    if (field.viewType === 'requirements-status') {
        const dataSource =
            typeof field.viewProps?.dataSource === 'string'
                ? field.viewProps.dataSource
                : '';
        const sourceData = dataSource ? getValue(dataSource) : undefined;
        const payload = sourceData as StatusListData | undefined;

        if (!payload || typeof payload !== 'object') {
            return null;
        }

        return <RequirementStatusView data={payload} />;
    }

    if (field.viewType === 'context-list') {
        const dataSource =
            typeof field.viewProps?.dataSource === 'string'
                ? field.viewProps.dataSource
                : '';
        const sourceData = dataSource ? getValue(dataSource) : undefined;
        const allItems = Array.isArray(sourceData) ? sourceData : [];
        const dismissable = !!field.viewProps?.dismissable;
        const excludedKey = dataSource ? `_excluded_${dataSource}` : '';
        const excludedIndices: number[] =
            dismissable && excludedKey
                ? (getValue(excludedKey) as number[]) || []
                : [];

        // Build filtered list while keeping track of each item's
        // original index so the dismiss handler can record the right
        // position regardless of prior exclusions.
        const indexedItems = allItems
            .map((item, i) => ({ item, originalIndex: i }))
            .filter(
                ({ originalIndex }) => !excludedIndices.includes(originalIndex)
            );

        const handleRemoveItem =
            dismissable && updateData && excludedKey
                ? (visibleIndex: number) => {
                      const entry = indexedItems[visibleIndex];
                      if (!entry) return;
                      const current = (getValue(excludedKey) as number[]) || [];
                      if (!current.includes(entry.originalIndex)) {
                          updateData(excludedKey, [
                              ...current,
                              entry.originalIndex,
                          ]);
                      }
                  }
                : undefined;

        return (
            <ContextListView
                data={indexedItems.map(({ item }) => item)}
                displayFields={field.viewProps?.displayFields || []}
                emptyMessage={field.viewProps?.emptyMessage}
                description={field.viewProps?.description}
                onRemoveItem={handleRemoveItem}
            />
        );
    }

    return null;
};
